import { observable, reaction } from 'mobx';
import { FitStationMedicalSolutionData, FitStationMedicalSolutionDataInput, FitStationProfile, Patient, User } from '@/store/engine/openapi/generated';
import { Store } from '@/store/mainStore';
import { optionalUpdateData } from '@/store/engine/modules/ProfileModule';
import UtilsStore from '@/store/utilsStore';
import { GetShoeSizes, SHOE_SIZE_UNIT, SHOE_TYPES, ShoeSizeUnit, ShoeType } from '@/store/ShoeMeasurements';
import Prescription from './prescription/prescription-types';
import { orthoticStyleMapping } from './leroyUtils/uxContextDataDefinition';

export default class PatientStore {
	@observable public patient?: Patient & { fitstationProfile?: FitStationProfile };
	@observable public isDirty: boolean = false;
	@observable public currentSessionID: string = '';

	@observable public patientUIData: PatientUIData = this.newPatientUIData();
	store: Store;
	updateMedicalProfileRequest = 0;

	// this is for keeping compatibility with old state
	get userRecordID() {
		return this.patient?.user?.userID ? this.patient.user.userID : '';
	}

	// this is for keeping compatibility with old state
	get patientLoading() {
		return !this.patient;
	}

	constructor(store: Store) {
		this.store = store;

		reaction(
			() => this.patientUIData.shoeUnit.shoeUnit,
			() => {
				this.validateShoeSize();
			}
		);

		reaction(
			() => this.patientUIData.shoeUnit.shoeUnitLR.L,
			() => {
				this.validateShoeSize();
			}
		);

		reaction(
			() => this.patientUIData.shoeUnit.shoeUnitLR.R,
			() => {
				this.validateShoeSize();
			}
		);
	}

	public async fetchPatientData(userRecordID: string) {
		const patient = await this.store.doctor.getPatientFromID(userRecordID);
		if (!patient || !patient.user) {
			throw new Error(`Patient of id ${userRecordID} not found.`);
		}
		this.patient = patient;
		if (this.patient) {
			this.patient.fitstationProfile = await this.getFitstationProfile(patient.user.userID);
		}
	}

	public async openPatient(userRecordID: string, orderID?: string | null) {
		await this.fetchPatientData(userRecordID);
		// HACK: No we don't
		// await this.createNewScanningSession(userRecordID); // it is always needed?

		if (!this.patient) {
			throw new Error(`Patient of id ${userRecordID} not found.`);
		}

		// if we are entering through router id, and the patient has order, we set the order instead
		if (UtilsStore.getHighestPriorityStatus(this.patient)?.includes('ORDER')) {
			if (!orderID) {
				const sessionStatusData = JSON.parse(UtilsStore.getOrderSession(this.patient)?.data);
				orderID = sessionStatusData?.['order_id'];
			}
			if (orderID) {
				await this.store.order.loadFromOrder(orderID as string);
				return;
			}
		}

		if (this.patient?.user) {
			this.fillUIDataWithUserProfile(this.patient.user);
			await this.setAssignedDoctor(this.patient.user?.operators?.currentOwner);
		}

		if (this.patient?.medicalSolutionData) {
			await this.fillUIDataWithMedicalProfile(this.patient.medicalSolutionData);
			this.store.leroy.setMetadataFromBE(this.patient.medicalSolutionData.leroyMeta);
		}

		if (this.patient?.fitstationProfile?.data?.lastValidScanningSession) {
			if (!this.currentSessionID) {
				this.currentSessionID = this.patient?.fitstationProfile?.data?.lastValidScanningSession;
			}
			this.store.biometric.lastValidScanningSessionID = this.patient.fitstationProfile?.data?.lastValidScanningSession;
		}
	}

	async getMedicalProfile(userRecordID: string) {
		const medicalProfile = await this.store.engine.profile.getFitstationMedicalProfile(userRecordID);

		if (!medicalProfile || !medicalProfile.data) {
			throw new Error('Error while getting medical profile');
		}

		return medicalProfile;
	}

	async getFitstationProfile(userRecordID: string) {
		const profile = await this.store.engine.profile.getFitstationProfile(userRecordID);
		return profile;
	}

	public fillUIDataWithOrderData(order: any) {
		const { userID, firstName, lastName, gender, flags, items, trackingLink } = order;
		if (!userID || !firstName || !lastName || !gender) throw new Error('Missing order data');

		// this.userRecordID = userID;
		// this.patientUIData.firstName = firstName;
		// this.patientUIData.lastName = lastName;
		this.patientUIData.gender = gender.toLocaleLowerCase();
		this.patientUIData.trackingLink = trackingLink || '';
		this.patientUIData.expertReview = flags?.expertReview || false;

		// Extract sizing data from ordered items
		if (items) {
			items.forEach((item) => {
				if (item && item.product) {
					if (item.quantity) {
						this.store.order.quantity = item.quantity;
					}
				}
			});
		}
	}

	public async fillUIDataWithMedicalProfile(profile: FitStationMedicalSolutionData) {
		const medicalData = profile;
		if (!medicalData) {
			return;
		}

		this.patientUIData.notes = medicalData.notes || ' ';
		this.patientUIData.zebraID = medicalData.prescriptionObj?.uiContextData?.zebraID || '';
		this.patientUIData.expertReview = medicalData.prescriptionObj?.uiContextData?.expertReview || false;
		this.patientUIData.patientID = profile.patientID || '';

		if (profile.birthDateTimestamp) {
			this.parseUserBirthdayTimestamp(profile.birthDateTimestamp);
		}

		await this.store.prescription.getPrescription(medicalData.prescriptionObj as Prescription);

		if (medicalData.prescriptionObj) {
			this.store.prescription.newPrescription = medicalData.prescriptionObj as Prescription;
			// TODO: DEMOHACK!!!!!
			if (this.store.prescription.newPrescription.accommodations) {
				this.store.prescription.newPrescription.accommodations.balancePad = this.store.prescription.newPrescription.accommodations.balancePad ?? {
					left: [],
					right: []
				};
				this.store.prescription.newPrescription.accommodations.neuromaPad = this.store.prescription.newPrescription.accommodations.neuromaPad ?? {
					left: [],
					right: []
				};
			}
		}

		this.patientUIData.weight.value = medicalData.weight || 0;
		this.patientUIData.weight.type = medicalData.weightUnit === 'kg' ? 'kg' : 'lbs';
		if (!(medicalData.leftGenderShoeSize && medicalData.rightGenderShoeSize)) {
			return;
		}

		if (medicalData.leftShoeSize && medicalData.rightShoeSize) {
			// Check if different sizes
			this.patientUIData.shoeSizes.differentSizes = true;
			this.patientUIData.shoeSizes.shoeSizeLR.L = medicalData.leftShoeSize;
			this.patientUIData.shoeSizes.shoeSizeLR.R = medicalData.rightShoeSize;
			this.patientUIData.shoeType.shoeTypeLR.L = (medicalData.leftShoeWidth as ShoeType) || SHOE_TYPES[0];
			this.patientUIData.shoeType.shoeTypeLR.R = (medicalData.rightShoeWidth as ShoeType) || SHOE_TYPES[0];
			this.patientUIData.shoeUnit.shoeUnitLR.L = medicalData.leftGenderShoeSize.includes('WOMEN')
				? SHOE_SIZE_UNIT[1]
				: medicalData.leftGenderShoeSize.includes('MEN')
				? SHOE_SIZE_UNIT[0]
				: SHOE_SIZE_UNIT[2];
			this.patientUIData.shoeUnit.shoeUnitLR.R = medicalData.rightGenderShoeSize.includes('WOMEN')
				? SHOE_SIZE_UNIT[1]
				: medicalData.rightGenderShoeSize.includes('MEN')
				? SHOE_SIZE_UNIT[0]
				: SHOE_SIZE_UNIT[2];
		} else {
			this.patientUIData.shoeSizes.differentSizes = false;
			this.patientUIData.shoeSizes.shoeSize = medicalData.shoeSize || 11;
			this.patientUIData.shoeType.shoeType = (medicalData.shoeWidth as ShoeType) || SHOE_TYPES[0];
			this.patientUIData.shoeUnit.shoeUnit = medicalData.leftGenderShoeSize.includes('WOMEN')
				? SHOE_SIZE_UNIT[1]
				: medicalData.leftGenderShoeSize.includes('MEN')
				? SHOE_SIZE_UNIT[0]
				: SHOE_SIZE_UNIT[2];
		}
	}

	public async fillUIDataWithMedicalProfileForOrder(profile: FitStationMedicalSolutionData, zebraID: string | undefined) {
		const medicalData = profile;
		if (!medicalData) {
			return;
		}

		this.patientUIData.notes = medicalData.notes || ' ';
		this.patientUIData.patientID = profile.patientID || '';

		if (profile.birthDateTimestamp) {
			this.parseUserBirthdayTimestamp(profile.birthDateTimestamp);
		}
		this.patientUIData.weight.value = medicalData.weight || 0;
		this.patientUIData.weight.type = medicalData.weightUnit === 'kg' ? 'kg' : 'lbs';
		if (!(medicalData.leftGenderShoeSize && medicalData.rightGenderShoeSize)) {
			return;
		}
		if (zebraID) {
			this.patientUIData.zebraID = zebraID;
		} else {
			this.patientUIData.zebraID = medicalData.prescriptionObj?.uiContextData?.zebraID || '';
		}
	}

	public fillUICContextDataFromOrderAttachment(uiContextData: any) {
		// this.patientUIData.shoeSizes.differentSizes = !!items[0] && !!items[1];
		if (uiContextData.shoeSize?.left && uiContextData.shoeSize?.right) {
			if (uiContextData.shoeSize?.left !== uiContextData.shoeSize?.right) {
				this.patientUIData.shoeSizes.shoeSizeLR.L = +uiContextData.shoeSize?.left;
				this.patientUIData.shoeSizes.shoeSizeLR.R = +uiContextData.shoeSize?.right;
				this.patientUIData.shoeSizes.differentSizes = true;
			} else {
				this.patientUIData.shoeSizes.shoeSize = +uiContextData.shoeSize?.right;
				this.patientUIData.shoeSizes.differentSizes = false;
			}
		}

		this.patientUIData.shoeType.shoeTypeLR.L = (uiContextData.shoeWidth?.left as ShoeType) || SHOE_TYPES[0];
		this.patientUIData.shoeType.shoeTypeLR.R = (uiContextData.shoeWidth?.right as ShoeType) || SHOE_TYPES[0];
		this.patientUIData.shoeUnit.shoeUnitLR.L = uiContextData.shoeStandard?.left?.includes('YOUTH')
			? SHOE_SIZE_UNIT[2]
			: uiContextData.shoeStandard?.left?.includes('WOMEN')
			? SHOE_SIZE_UNIT[1]
			: SHOE_SIZE_UNIT[0];

		this.patientUIData.shoeUnit.shoeUnitLR.R = uiContextData.shoeStandard?.right?.includes('YOUTH')
			? SHOE_SIZE_UNIT[2]
			: uiContextData.shoeStandard?.right?.includes('WOMEN')
			? SHOE_SIZE_UNIT[1]
			: SHOE_SIZE_UNIT[0];
		// HACK: if (items[0] && !items[1]) {
		this.patientUIData.shoeType.shoeType = (uiContextData.shoeWidth?.left as ShoeType) || SHOE_TYPES[0];
		this.patientUIData.shoeUnit.shoeUnit = uiContextData.shoeStandard?.left?.includes('YOUTH')
			? SHOE_SIZE_UNIT[2]
			: uiContextData.shoeStandard?.left?.includes('WOMEN')
			? SHOE_SIZE_UNIT[1]
			: SHOE_SIZE_UNIT[0];
		// HACK: }
	}

	public fillUIDataWithUserProfile(user: User) {
		this.patientUIData.firstName = user.firstName ?? '';
		this.patientUIData.lastName = user.lastName ?? '';
		this.patientUIData.gender = user.gender ?? 'unknown';
	}

	public markAsDirty() {
		this.isDirty = true;
		if (this.updateMedicalProfileRequest) {
			clearTimeout(this.updateMedicalProfileRequest);
		}
		this.updateMedicalProfileRequest = setTimeout(async () => {
			await this.updateMedicalProfile();
			this.isDirty = false;
		}, 1000);
	}

	public async setAssignedDoctor(doctorID?: string | null) {
		if (!doctorID) {
			this.patientUIData.assignedToDoctor = '';
			return;
		}
		this.patientUIData.assignedToDoctor = doctorID;
		const isDoctorAvailable = await this.store._patient.checkDoctorValidity();
		if (!isDoctorAvailable) {
			this.patientUIData.assignedToDoctor = '';
		}
	}

	public async createPatient() {
		this.store.leroy.restart(true);
		this.store.biometric.clear('both');
		const profile = await this.store.engine.profile;
		const userRecord = await this.store.engine.profile.createUserRecord({
			firstName: this.patientUIData.firstName,
			lastName: this.patientUIData.lastName,
			gender: this.patientUIData.gender
		});

		const userRecordID = userRecord?.userID;

		if (!userRecordID) throw new Error('Failed to create new user record');
		await this.store.engine.store.addUserToSite(userRecordID);
		const session = await this.createNewScanningSession(userRecordID);
		await profile.updateFitStationProfile(userRecordID, {
			lastValidScanningSession: session,
			weight: 70
		});
		const fitstationProfile = await this.store.engine.profile.getFitstationProfile(userRecordID);

		await profile.updateUserOwner(userRecordID, this.store._patient.patientUIData.assignedToDoctor);
		let profileInput = await this.updateMedicalProfile({ update: true }, userRecordID);
		const medicalProfile = await this.getMedicalProfile(userRecordID);
		this.patient = {
			user: userRecord as User,
			medicalSolutionData: medicalProfile.data,
			fitstationProfile: fitstationProfile
		};
		this.store.biometric.lastValidScanningSessionID = session;
		return this.patient;
	}

	public async clonePatient() {
		if (!this.userRecordID) {
			return;
		}

		let oldUserID = this.userRecordID;
		let oldSessionId = this.store.biometric.lastValidScanningSessionID;

		const profile = await this.store.engine.profile;
		const userRecord = await this.store.engine.profile.createUserRecord(this.patientUIData);

		const userRecordID = userRecord?.userID;

		if (!userRecordID) throw new Error('Failed to create new user record');

		const session = await this.createNewScanningSession(userRecordID);

		const fitstationProfile = await this.store.engine.profile.copyFitstationProfile(userRecordID, oldUserID, session, oldSessionId);

		await this.store.engine.store.addUserToSite(userRecordID);

		await profile.updateUserOwner(userRecordID, this.store._patient.patientUIData.assignedToDoctor);

		let profileInput = await this.updateMedicalProfile({ update: true }, userRecordID);
		const medicalProfile = await this.getMedicalProfile(userRecordID);
		this.patient = {
			user: userRecord as User,
			medicalSolutionData: medicalProfile.data,
			fitstationProfile: fitstationProfile
		};

		await this.patientFlowSkip(this.patient);

		return this.patient;
	}

	private async patientFlowSkip(patient: Patient) {
		try {
			this.store.leroy.restart(true);
			if (patient.user) {
				await this.openPatient(patient.user?.userID);
			}
		} catch (e) {
			console.error(e);
			return;
		}

		const fp = await this.getFitstationProfile(this.userRecordID);

		const scans = await this.store.engine.biometric.getScans(fp.data?.lastValidScanningSession);
	}

	public async createNewScanningSession(userRecordID: string) {
		const session = await this.store.engine.profile.createSession(userRecordID);
		if (!session) {
			throw new Error('Error while creating session');
		}

		this.currentSessionID = session;
		return this.currentSessionID;
	}

	public async clear() {
		this.store.agent.oneFootScan = false;
		this.store.agent.scannedInThisSession = false;
		this.patientUIData = this.newPatientUIData();
		this.store.biometric.clear();
		this.patient = {
			user: undefined,
			medicalSolutionData: undefined,
			fitstationProfile: undefined,
			sessionsStatuses: undefined
		};
	}

	public async updateMedicalProfile(optional?: optionalUpdateData, userRecordID?: string) {
		if (!this.userRecordID && !userRecordID) {
			return;
		}
		if (!optional) {
			optional = {} as optionalUpdateData;
		}
		this.isDirty = true;
		let profileInput: FitStationMedicalSolutionDataInput = {
			patientID: this.patientUIData.patientID,
			weight: +this.patientUIData.weight.value,
			weightUnit: this.patientUIData.weight.type,
			shoeSize: this.patientUIData.shoeSizes.differentSizes ? undefined : this.patientUIData.shoeSizes.shoeSize,
			shoeWidth: this.patientUIData.shoeSizes.differentSizes ? undefined : this.patientUIData.shoeType.shoeType,
			birthDateTimestamp: this.birthdayTimestamp(),
			shoeSizeUnit: 'US',
			leftShoeSize: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeSizes.shoeSizeLR.L : undefined,
			leftShoeWidth: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeType.shoeTypeLR.L : undefined,
			rightShoeSize: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeSizes.shoeSizeLR.R : undefined,
			rightShoeWidth: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeType.shoeTypeLR.R : undefined,
			leftGenderShoeSize: this.patientUIData.shoeSizes.differentSizes
				? this.patientUIData.shoeUnit.shoeUnitLR.L.replace('US ', '')
				: this.patientUIData.shoeUnit.shoeUnit.replace('US ', ''),
			rightGenderShoeSize: this.patientUIData.shoeSizes.differentSizes
				? this.patientUIData.shoeUnit.shoeUnitLR.R.replace('US ', '')
				: this.patientUIData.shoeUnit.shoeUnit.replace('US ', ''),
			notes: this.patientUIData.notes ? this.patientUIData.notes : ' ',
			leroyMeta: JSON.stringify(this.store.leroy.leroyManager.metadata),
			prescriptionObj: {
				...this.store.prescription.newPrescription,
				uiContextData: {
					zebraID: this.patientUIData.zebraID,
					expertReview: this.patientUIData.expertReview,
					orthoticStyle: this.store.prescription.newPrescription.uiContextData.orthoticStyle,
					orthoticStyleTitle: this.store.product.currentSavedRXTitle,
					pack: this.store.product.pack,
					quantity: this.store.order.quantity,
					scanMode: this.store.agent.scanMode.toLowerCase(),
					shoeSize: {
						left: (this.patientUIData.shoeSizes.differentSizes
							? this.patientUIData.shoeSizes.shoeSizeLR.L
							: this.patientUIData.shoeSizes.shoeSize
						).toString(),
						right: (this.patientUIData.shoeSizes.differentSizes
							? this.patientUIData.shoeSizes.shoeSizeLR.R
							: this.patientUIData.shoeSizes.shoeSize
						).toString()
					},
					shoeStandard: {
						left: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeUnit.shoeUnitLR.L : this.patientUIData.shoeUnit.shoeUnit,
						right: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeUnit.shoeUnitLR.R : this.patientUIData.shoeUnit.shoeUnit
					},
					shoeWidth: {
						left: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeType.shoeTypeLR.L : this.patientUIData.shoeType.shoeType,
						right: this.patientUIData.shoeSizes.differentSizes ? this.patientUIData.shoeType.shoeTypeLR.R : this.patientUIData.shoeType.shoeType
					},
					weight: {
						unit: this.patientUIData.weight.type,
						value: this.patientUIData.weight.value.toString()
					}
				}
			}
		};
		await this.store.engine.profile.updateFitstationMedicalProfile(userRecordID ? userRecordID : this.userRecordID, profileInput, { ...optional });
		this.isDirty = false;
		return profileInput;
	}

	public async checkDoctorValidity() {
		await this.store.engine.store.getDoctorsByStoreCache();
		const doctors: { userID: string }[] = this.store.engine.store.doctors;
		return doctors.some((d) => d.userID === this.patientUIData.assignedToDoctor);
	}

	public async preparePDF(orderResult?): Promise<string> {
		await this.store.prescription.savePrescription();
		await new Promise((r) => setTimeout(r, 500));
		const profile = await this.store.engine.profile;

		const session = this.patient?.fitstationProfile?.data?.lastValidScanningSession;
		let base64PDF = async (): Promise<string> => {
			if (this.store.order.order?.orderID) {
				return await this.store.engine.order.getOrderAttachment(this.store.order.order.orderID, 'pres.pdf').then((p) => p || '');
			} else {
				const d = new Date();
				const offset = d.getTimezoneOffset() * 60000;
				const pdfInput = {
					userID: this.userRecordID,
					sessionID: session,
					employeeID: this.store.doctor.doctorUser?.userID,
					storeID: this.store.engine.store.getStore.storeID,
					prescriptionID: '',
					sizeGender: this.patientUIData.shoeUnit.shoeUnit === "US MEN'S" ? 'male' : 'female',
					orderCreatedAt: orderResult ? Math.floor((Date.now() - offset) / 1000) : undefined,
					orderID: orderResult ? orderResult.orderID : undefined
				};

				//@ts-ignore
				return await profile.createPDF(pdfInput).then((p) => (p && p.data) || '');
			}
		};

		return await base64PDF();
	}

	private validateShoeSize() {
		const getShoeSizes = GetShoeSizes;
		if (this.patientUIData.shoeSizes.shoeSize > getShoeSizes(this.patientUIData.shoeUnit.shoeUnit).slice(-1)[0]) {
			this.patientUIData.shoeSizes.shoeSize = getShoeSizes(this.patientUIData.shoeUnit.shoeUnit).slice(-1)[0];
		}
		if (this.patientUIData.shoeSizes.shoeSizeLR.L > getShoeSizes(this.patientUIData.shoeUnit.shoeUnitLR.L).slice(-1)[0]) {
			this.patientUIData.shoeSizes.shoeSizeLR.L = getShoeSizes(this.patientUIData.shoeUnit.shoeUnitLR.L).slice(-1)[0];
		}
		if (this.patientUIData.shoeSizes.shoeSizeLR.R > getShoeSizes(this.patientUIData.shoeUnit.shoeUnitLR.R).slice(-1)[0]) {
			this.patientUIData.shoeSizes.shoeSizeLR.R = getShoeSizes(this.patientUIData.shoeUnit.shoeUnitLR.R).slice(-1)[0];
		}
		if (this.patientUIData.shoeUnit.shoeUnit === "US YOUTH'S") {
			this.patientUIData.shoeType.shoeType = 'standard';
		}
		if (this.patientUIData.shoeUnit.shoeUnitLR.L === "US YOUTH'S") {
			this.patientUIData.shoeType.shoeTypeLR.L = 'standard';
		}
		if (this.patientUIData.shoeUnit.shoeUnitLR.R === "US YOUTH'S") {
			this.patientUIData.shoeType.shoeTypeLR.R = 'standard';
		}
	}

	private birthdayTimestamp() {
		if (this.patientUIData.birthDate.year != '' && this.patientUIData.birthDate.month != '' && this.patientUIData.birthDate.day != '')
			return Date.UTC(this.patientUIData.birthDate.year, this.patientUIData.birthDate.month - 1, this.patientUIData.birthDate.day);
		return -2232713290000;
	}

	private parseUserBirthdayTimestamp(timestamp: number) {
		const birthDate = new Date(+timestamp);
		if (+timestamp < -2209118287000) {
			this.patientUIData.birthDate = {
				day: '',
				month: '',
				year: ''
			};
		} else {
			this.patientUIData.birthDate = {
				day: birthDate.getUTCDate(),
				month: birthDate.getUTCMonth() + 1,
				year: birthDate.getUTCFullYear()
			};
		}
	}

	private newPatientUIData(): PatientUIData {
		return {
			birthDate: { day: '', month: '', year: '' },
			firstName: '',
			gender: 'unknown',
			lastName: '',
			patientID: '',
			shoeSizes: { differentSizes: false, shoeSize: 11, shoeSizeLR: { L: 11, R: 11 } },
			shoeType: { shoeType: SHOE_TYPES[0], shoeTypeLR: { L: SHOE_TYPES[0], R: SHOE_TYPES[0] } },
			shoeUnit: { shoeUnit: SHOE_SIZE_UNIT[0], shoeUnitLR: { L: SHOE_SIZE_UNIT[0], R: SHOE_SIZE_UNIT[0] } },
			weight: { type: 'lbs', value: 0 },
			notes: '',
			assignedToDoctor: '',
			trackingLink: '',
			expertReview: false,
			zebraID: ''
		};
	}
}

export type PatientUIData = {
	firstName: string;
	lastName: string;
	patientID: string;
	weight: { type: 'kg' | 'lbs'; value: number | '' };
	gender: string;
	birthDate: {
		day: number | '';
		month: number | '';
		year: number | '';
	};
	shoeSizes: { differentSizes: boolean; shoeSize: number; shoeSizeLR: { L: number; R: number } };
	shoeType: {
		shoeType: ShoeType;
		shoeTypeLR: { L: ShoeType; R: ShoeType };
	};
	shoeUnit: {
		shoeUnit: ShoeSizeUnit;
		shoeUnitLR: { L: ShoeSizeUnit; R: ShoeSizeUnit };
	};
	notes: string;
	assignedToDoctor: string;
	trackingLink: string;
	expertReview: boolean;
	zebraID: string;
};

export const WEIGHT_TYPE = ['kg', 'lbs'] as const;
export type WeightType = typeof WEIGHT_TYPE[number];
